import React from "react";
import PropTypes from "prop-types";
import EmptyValue from "./EmptyValue";

// StatusSelect — the legacy inline status dropdown, as a controlled presentational
// component. The v2 Orders/B2B tables render status as a read-only badge; the legacy
// Quotations table lets an ops user change it straight from the row. This restores that
// affordance without any data fetching: the caller owns the write.
//
// Markup copied verbatim from src/components/quotationsTable/table-instance.jsx:60-75 —
// the inline `border: 1px solid #b0b5ba` / `borderRadius: 6px` style object, the
// `w-[100%]` class and the `px-2 text-black` wrapper. Do not hand-convert those to
// Tailwind classes: 1rem is 13px app-wide, and `bg-white`/`text-white`/`text-md` emit no
// CSS in this build.
//
// onClick stops propagation because these rows open the v2 side panel on click —
// without it, opening the dropdown also opens the panel and the two fight each other.
// The change handler stops propagation for the same reason (belt and braces: some
// browsers fire a click on the <select> as an option is committed).
//
// UNKNOWN-VALUE GUARD (do not remove): a controlled <select> whose `value` matches no
// <option> renders BLANK. On a row whose stored status is real but simply not in the
// currently allowed list (a legacy status, or a list still narrowing by type), that makes
// the row look unset — and the next change silently overwrites a real status the user
// never saw. So when `value` is absent from `options` we append it as an extra option.
// This is the same bug that was found and fixed on the B2B cancel-comment column; it is
// not hypothetical.
//
// When `options` is empty (the allowed-status list is still loading, or this row's status
// is not editable) we render plain text instead of an empty dropdown, so the cell never
// degrades into a control with nothing in it.
function StatusSelect({ value, options, disabled, onChange }) {
    const hasValue = value !== null && value !== undefined && value !== "";
    const list = options || [];

    if (list.length === 0) {
        return (
            <div className="px-2 text-black">
                {hasValue ? <span className="font-sans">{value}</span> : <EmptyValue />}
            </div>
        );
    }

    // Unknown-value guard — see the note above.
    const renderedOptions = hasValue && !list.includes(value) ? [...list, value] : list;

    return (
        <div className="px-2 text-black">
            <select
                style={{
                    border: "1px solid #b0b5ba",
                    borderRadius: "6px",
                }}
                className="w-[100%]"
                // `value ?? ""` keeps this a controlled select even before a status
                // exists, instead of flipping between controlled and uncontrolled.
                value={hasValue ? value : ""}
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onChange={(e) => {
                    e.stopPropagation();
                    // Value first — callers write a status, they do not need the event.
                    // The event is passed second for the rare caller that does.
                    onChange(e.target.value, e);
                }}
            >
                {/* Only rendered while nothing is set, so the select does not silently
                    present the first option as if it were the stored value. */}
                {!hasValue && <option value="" />}
                {renderedOptions.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </div>
    );
}

StatusSelect.propTypes = {
    value: PropTypes.string,
    // Allowed statuses, as plain strings. May be empty while still loading.
    options: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
    // (nextValue, event) => void
    onChange: PropTypes.func,
};

StatusSelect.defaultProps = {
    value: "",
    options: [],
    disabled: false,
    onChange: () => {},
};

export default StatusSelect;
